import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import {
  HeaderRow,
  TitleContainer,
  SubTitle,
  Title,
} from '../../../../components/v2/Header';
import UserContext from '../../../../context/UserContext';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import useToast from '../../../hooks/useToast';
import { capturePoolQueueViewed } from '../../../utils/poolTelemetry';

import CoachTable from './components/CoachTable';
import PoolQueueSummary from './components/PoolQueueSummary';
import useDashboardCoachViews from './hooks/useDashboardCoachViews';
import {
  Container,
  StyledTabs,
} from './styles';
import texts from './texts.json';

const DashboardTab = {
  MY_COACHES: 'myCoaches',
  POOL_QUEUE: 'poolQueue',
};

const CoachingDashboard = () => {
  const [selectedTab, setSelectedTab] = useState(DashboardTab.MY_COACHES);

  const hasCapturedPoolQueueViewRef = useRef(false);

  const {
    userDoc: {
      firstName: ACName,
    },
  } = useContext(UserContext);
  const { userId: user } = useContext(LoggedInUserContext);

  const {
    myCoachViews,
    myPodIdByCoachId,
    poolCoachViews,
    poolQueueSummaries,
    poolQueueStats,
    isReady,
    loadError,
  } = useDashboardCoachViews();

  const { showToast } = useToast();

  const isPoolQueue = selectedTab === DashboardTab.POOL_QUEUE;

  useEffect(() => {
    if (loadError) {
      showToast(format(texts.errors.loadDashboard, { error: loadError.message }), { error: true });
    }
  }, [loadError, showToast]);

  useEffect(() => {
    if (!isPoolQueue) {
      hasCapturedPoolQueueViewRef.current = false;
      return;
    }

    if (isReady && !hasCapturedPoolQueueViewRef.current) {
      hasCapturedPoolQueueViewRef.current = true;
      capturePoolQueueViewed(user, poolQueueStats);
    }
  }, [
    isPoolQueue,
    isReady,
    user,
    poolQueueStats,
  ]);

  const tabsConfig = useMemo(() => ({
    [DashboardTab.MY_COACHES]: {
      label: texts.tabs.myCoaches,
    },
    [DashboardTab.POOL_QUEUE]: {
      label: texts.tabs.poolQueue,
      count: poolQueueStats.coachesWithOpenWorkCount,
    },
  }), [poolQueueStats.coachesWithOpenWorkCount]);

  return (
    <Container>
      <HeaderRow>
        <TitleContainer>
          <Title>{format(texts.title, { name: ACName })}</Title>
          <SubTitle>{isPoolQueue ? texts.poolSubTitle : texts.subTitle}</SubTitle>
        </TitleContainer>
      </HeaderRow>
      <StyledTabs
        tabsConfig={tabsConfig}
        selectedTab={selectedTab}
        onSelectTab={setSelectedTab}
      />
      {isPoolQueue ? (
        <>
          <PoolQueueSummary poolQueueStats={poolQueueStats} />
          <CoachTable
            views={poolCoachViews}
            workSummaries={poolQueueSummaries}
            isReady={isReady}
            hasLoadError={!!loadError}
            isPoolQueue
          />
        </>
      ) : (
        <CoachTable
          views={myCoachViews}
          podIdByCoachId={myPodIdByCoachId}
          isReady={isReady}
          hasLoadError={!!loadError}
        />
      )}
    </Container>
  );
};

export default compose(
  observer,
)(CoachingDashboard);
